import { Controller } from "stimulus";

const LIST_ID = "em-events-list"
const CREATE_LINK_ID = "em-create-event-link"
const LOADING_CLASS = "em-calendar--loading"
const LIST_LOADING_CLASS = "em-events-list--loading"
const SELECTED_DAY_CLASS = "fc-day--selected"

// Month-grid calendar for the EM dashboard event lists (Assigned Events + Schedule). Pills travel
// embedded in the list's data-pills attribute (never fetched separately), so month nav, day clicks,
// and pagination all swap the list fragment and the grid can never drift. FullCalendar loads lazily.
export default class extends Controller {
  static targets = ["grid"]
  static values = {
    initialDate: String,
    windowStart: String,
    windowEnd: String,
    day: String,
    listUrl: String,
    desktopQuery: String,
    todayLabel: String,
    moreLabel: String,
    createLabel: String
  }

  gridTarget: HTMLElement
  initialDateValue: string
  windowStartValue: string
  windowEndValue: string
  dayValue: string
  listUrlValue: string
  desktopQueryValue: string
  todayLabelValue: string
  moreLabelValue: string
  createLabelValue: string

  calendar: any
  eventSource: any
  listAbort: AbortController | null
  windowStart: string
  windowEnd: string
  selectedDay: string | null
  ready: boolean
  onBeforeCache: () => void
  onDocumentClick: (event: Event) => void

  connect() {
    this.onBeforeCache = () => this.teardown()
    document.addEventListener("turbolinks:before-cache", this.onBeforeCache)

    this.onDocumentClick = (event) => this.interceptPaginationClick(event)
    document.addEventListener("click", this.onDocumentClick)

    // Only mount (and so lazy-load FullCalendar) on desktop, where the query matches; on mobile a
    // viewport_redirect controller on the same element navigates to list mode, so the bundle never loads.
    if (window.matchMedia(this.desktopQueryValue).matches) this.mount()
  }

  disconnect() {
    document.removeEventListener("turbolinks:before-cache", this.onBeforeCache)
    document.removeEventListener("click", this.onDocumentClick)
    this.teardown()
  }

  async mount() {
    if (this.calendar) return

    let modules
    try {
      modules = await Promise.all([
        import("@fullcalendar/core"),
        import("@fullcalendar/daygrid"),
        import("@fullcalendar/interaction")
      ])
    } catch (error) {
      // A stale chunk (e.g. a deploy mid-session) can't load — leave the grid unmounted so
      // pagination falls back to normal navigation (interceptPaginationClick bails on no calendar).
      console.error("EM calendar failed to load", error)

      return
    }

    // Turbolinks may have navigated away while the bundle loaded; building on a detached
    // element would leak the grid and read the next page's list.
    if (!this.element.isConnected) return

    const [{ Calendar }, { default: dayGridPlugin }, { default: interactionPlugin }] = modules

    this.ready = false
    this.windowStart = this.windowStartValue
    this.windowEnd = this.windowEndValue
    this.selectedDay = this.dayValue || null

    this.calendar = new Calendar(this.gridTarget, {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: "dayGridMonth",
      initialDate: this.initialDateValue,
      firstDay: 0,
      fixedWeekCount: false,
      showNonCurrentDates: true,
      headerToolbar: { left: "title", right: "prev today next" },
      buttonText: { today: this.todayLabelValue },
      dayMaxEvents: 3,
      moreLinkContent: (arg: any) => `+${arg.num} ${this.moreLabelValue}`,
      eventDisplay: "block",
      dateClick: (info: any) => this.onDateClick(info),
      datesSet: (info: any) => this.onDatesSet(info),
      eventClick: (info: any) => this.onEventClick(info),
      eventClassNames: (arg: any) => this.pillClasses(arg),
      eventContent: (arg: any) => this.renderPill(arg)
    })

    this.calendar.render()
    this.setPills(this.currentPills())
    this.restoreSelectedDay()
  }

  // Destroys the grid and leaves the container pristine — also runs on turbolinks:before-cache,
  // because Turbolinks snapshots the page before disconnect() and would otherwise cache the
  // rendered grid, duplicating it on a restore visit. Both loading overlays are cleared here too:
  // a fetch in flight at snapshot time would otherwise freeze its spinner into the cached page and
  // show it over the already-rendered events when Turbolinks previews that page on the way back.
  teardown() {
    if (!this.calendar) return

    this.calendar.destroy()
    this.calendar = null
    this.eventSource = null
    this.setLoading(false)
    this.listLoading(false)
  }

  setPills(pills: object[]) {
    if (this.eventSource) this.eventSource.remove()

    this.eventSource = this.calendar.addEventSource(pills || [])
  }

  currentPills(): object[] {
    const list = document.getElementById(LIST_ID)

    return list ? JSON.parse(list.dataset.pills || "[]") : []
  }

  // A day arriving in the URL (shared/refreshed link) re-selects its cell and prefills the
  // create link, mirroring the server's day-scoped list.
  restoreSelectedDay() {
    if (!this.selectedDay) return

    const dayEl = this.gridTarget.querySelector(`[data-date="${this.selectedDay}"]`) as HTMLElement | null

    this.updateCreateLink(this.selectedDay)
    this.highlightDay(dayEl)
  }

  // Only an actual window change refreshes the list — FullCalendar can emit datesSet more than
  // once while settling layout, and the initial window is already server-rendered (ready guards it).
  onDatesSet(info: any) {
    const start = this.isoDate(info.view.activeStart)
    const end = this.isoDate(this.lastVisibleDay(info.view.activeEnd))
    const windowChanged = start !== this.windowStart || end !== this.windowEnd

    this.windowStart = start
    this.windowEnd = end

    this.syncFormWindows()

    if (this.ready && windowChanged) {
      this.selectedDay = null
      this.highlightDay(null)
      this.updateCreateLink(null)
      this.setLoading(true)
      this.listLoading(true)
      this.fetchList(this.listUrl(null))
      this.syncUrl()
    }

    this.ready = true
  }

  // A click on the selected day deselects it; any other day selects it. Deselecting widens the
  // list back to the window scope — the same request a month nav fires.
  onDateClick(info: any) {
    // The in-cell create link sits inside the day cell, so FullCalendar reports a click on it
    // as a date click. Let the link navigate on its own — running the deselect here fires
    // syncUrl mid-navigation, which stamps the window params onto the create page's URL.
    if (info.jsEvent.target.closest(".em-calendar-day-create")) return

    const deselecting = info.dayEl.classList.contains(SELECTED_DAY_CLASS)

    this.selectedDay = deselecting ? null : info.dateStr
    this.updateCreateLink(this.selectedDay)
    this.highlightDay(deselecting ? null : info.dayEl)
    this.listLoading(true)
    this.fetchList(this.listUrl(this.selectedDay))
    this.syncUrl(this.selectedDay)
  }

  // Pagination inside the swapped list stays a live fetch+swap — the links already carry
  // window/day/sort/filter params, so their hrefs are fetched as-is.
  interceptPaginationClick(event: Event) {
    // No grid (mobile, or the bundle failed to load) — let the link navigate normally instead
    // of swapping the list and calling setPills on a null calendar.
    if (!this.calendar) return

    const target = event.target as HTMLElement
    const link = target.closest("a.page-link") as HTMLAnchorElement | null

    if (!link || !link.closest(`#${LIST_ID}`)) return

    event.preventDefault()

    // A first/last-page arrow is still a real anchor (Bootstrap only greys its .page-item via
    // .disabled) — make a click on a disabled arrow a no-op instead of fetching a clamped page 1.
    const item = link.closest(".page-item")

    if (item && item.classList.contains("disabled")) return

    this.listLoading(true)
    this.fetchList(link.pathname + link.search)
  }

  async fetchList(url: string) {
    if (this.listAbort) this.listAbort.abort()
    this.listAbort = new AbortController()

    try {
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
        signal: this.listAbort.signal
      })

      if (!response.ok) throw new Error(`List fetch failed: ${response.status}`)

      this.swapList(await response.text())
    } catch (error) {
      if (error.name === "AbortError") return

      // A superseded fetch already cleared its spinner; here we only reach a real failure, so
      // clear the overlays and leave the list on its prior content (no caller catches this).
      console.error("EM calendar list fetch failed", error)
      this.setLoading(false)
      this.listLoading(false)
    }
  }

  // The current page's query params (sort, program filter) carry over; the window params are
  // re-pointed at the viewed window and pagination resets, since the old page number belonged
  // to the old scope.
  listUrl(day: string | null): string {
    const url = new URL(this.listUrlValue, window.location.origin)

    new URLSearchParams(window.location.search).forEach((value, key) => url.searchParams.set(key, value))
    this.applyWindowParams(url.searchParams, day)

    return url.pathname + url.search
  }

  // Points a URLSearchParams at the current window: the visible month, no stale page, and the
  // selected day only when one is set. Shared by the list fetch and the address-bar sync.
  applyWindowParams(params: URLSearchParams, day: string | null) {
    params.set("window_start", this.windowStart)
    params.set("window_end", this.windowEnd)
    params.delete("page")
    params.delete("day")

    if (day) params.set("day", day)
  }

  swapList(html: string) {
    const list = document.getElementById(LIST_ID)

    if (!list) return

    list.outerHTML = html
    this.setPills(this.currentPills())
    this.setLoading(false)
    window.initBSN()
  }

  // Keeps the address bar shareable/refreshable without Turbolinks visits. history.state is
  // preserved so Turbolinks restore visits keep working.
  syncUrl(day: string | null = null) {
    const url = new URL(window.location.href)

    this.applyWindowParams(url.searchParams, day)
    history.replaceState(history.state, "", url.pathname + url.search)
  }

  // Keeps the sort/program-filter forms pointed at the month being viewed, so submitting them
  // re-renders THIS month instead of snapping back to the initial one.
  syncFormWindows() {
    this.setFormWindowFields("window_start", this.windowStart)
    this.setFormWindowFields("window_end", this.windowEnd)
  }

  setFormWindowFields(name: string, value: string) {
    document.querySelectorAll<HTMLInputElement>(`form input[name="${name}"]`).forEach((field) => {
      field.value = value
    })
  }

  highlightDay(dayEl: HTMLElement | null) {
    this.gridTarget.querySelectorAll(`.${SELECTED_DAY_CLASS}`).forEach((el) => {
      el.classList.remove(SELECTED_DAY_CLASS)
    })
    this.gridTarget.querySelectorAll(".em-calendar-day-create").forEach((el) => el.remove())

    if (!dayEl) return

    dayEl.classList.add(SELECTED_DAY_CLASS)
    this.injectDayCreateLink(dayEl)
  }

  // Mirrors the header create button inside the selected cell (Figma's "+ Create Event") —
  // absent for viewers who can't create events, exactly like the header button.
  injectDayCreateLink(dayEl: HTMLElement) {
    const source = document.getElementById(CREATE_LINK_ID) as HTMLAnchorElement | null
    const frame = dayEl.querySelector(".fc-daygrid-day-frame")

    if (!source || !frame) return

    const link = document.createElement("a")

    link.className = "em-calendar-day-create"
    link.textContent = this.createLabelValue
    link.href = source.href
    frame.append(link)
  }

  updateCreateLink(day: string | null) {
    const link = document.getElementById(CREATE_LINK_ID) as HTMLAnchorElement | null

    if (!link) return

    const url = new URL(link.href, window.location.origin)

    if (day) {
      url.searchParams.set("date", day)
    } else {
      url.searchParams.delete("date")
    }

    link.setAttribute("href", url.pathname + url.search)
  }

  onEventClick(info: any) {
    info.jsEvent.preventDefault()

    if (info.event.url) window.location.assign(info.event.url)
  }

  pillClasses(arg: any): string[] {
    const role = arg.event.extendedProps.role || "none"

    return [`em-calendar-pill--${role}`]
  }

  renderPill(arg: any): { domNodes: HTMLElement[] } {
    const { startLabel, repeat } = arg.event.extendedProps
    const pill = document.createElement("div")

    pill.className = "em-calendar-pill"
    pill.title = arg.event.title

    const dot = document.createElement("span")

    dot.className = "em-calendar-pill-dot"

    const time = document.createElement("span")

    time.className = "em-calendar-pill-time"
    time.textContent = startLabel

    const title = document.createElement("span")

    title.className = "em-calendar-pill-title"
    title.textContent = arg.event.title

    pill.append(dot, time, title)

    if (repeat) {
      const icon = document.createElement("span")

      icon.className = "mdi mdi-repeat em-calendar-pill-repeat"
      pill.append(icon)
    }

    return { domNodes: [pill] }
  }

  setLoading(isLoading: boolean) {
    this.element.classList.toggle(LOADING_CLASS, isLoading)
  }

  listLoading(isLoading: boolean) {
    const list = document.getElementById(LIST_ID)

    if (list) list.classList.toggle(LIST_LOADING_CLASS, isLoading)
  }

  // activeEnd is exclusive (midnight after the last visible cell) — step back one day to get
  // the inclusive last visible day the server expects.
  lastVisibleDay(activeEnd: Date): Date {
    const last = new Date(activeEnd)

    last.setDate(last.getDate() - 1)

    return last
  }

  // Local date getters, NOT getUTC*: FullCalendar hands out local-midnight dates here, and UTC
  // getters shift the calendar date in UTC-positive zones.
  isoDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")

    return `${date.getFullYear()}-${month}-${day}`
  }
}
